import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneRight, PhoneCall, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import api from "../lib/api";
import { toast } from "sonner";

const genId = () => "yad_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

const AssistantJose = () => {
  const { user } = useAuth();
  const { lang } = useT();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCallback, setShowCallback] = useState(false);
  const [cbSent, setCbSent] = useState(false);
  const [cbSending, setCbSending] = useState(false);
  const [cb, setCb] = useState(() => {
    const parts = (user?.name || "").split(" ");
    return { first: parts[0] || "", last: parts.slice(1).join(" ") || "", phone: user?.phone || "", email: user?.email || "" };
  });
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("yad_chat_session");
    if (!s) { s = genId(); localStorage.setItem("yad_chat_session", s); }
    return s;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      const first = user && user.name ? user.name.split(" ")[0] : null;
      const greet = first
        ? `Bonjour ${first} 👋 Je suis José, votre conseiller YADIMMO. Que puis-je faire pour votre projet d'investissement au Maroc ?`
        : "Bonjour 👋 Je suis José, votre conseiller YADIMMO. Vous cherchez à investir au Maroc ? Dites-moi votre projet et je vous oriente vers le bon bien.";
      setMessages([{ role: "assistant", text: greet }]);
    }
  }, [open, user, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sessionId, message: text, lang });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Tous nos conseillers sont actuellement occupés. Laissez-moi vos coordonnées et nous vous rappelons très vite 📞" }]);
      setShowCallback(true);
    } finally {
      setLoading(false);
    }
  };

  const submitCallback = async () => {
    if (!cb.first.trim() || !cb.phone.trim()) { toast.error("Prénom et téléphone requis"); return; }
    setCbSending(true);
    try {
      await api.post("/contact", {
        name: `${cb.first} ${cb.last}`.trim(), email: cb.email || "rappel@yadimmo.ma",
        phone: cb.phone, message: "Demande de rappel via le chat José (conseillers occupés).",
        subject: "callback",
      });
      setCbSent(true);
    } catch {
      toast.error("Erreur, réessayez.");
    } finally {
      setCbSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="assistant-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 end-5 z-[60] flex items-center gap-3 bg-navy text-white ps-2 pe-5 py-2 rounded-full shadow-2xl hover:bg-[#254B75] transition-colors group"
      >
        <img src="/jose.png" alt="José" className="w-11 h-11 rounded-full object-cover border-2 border-gold" />
        <span className="text-sm font-semibold hidden sm:block">Discuter avec José</span>
        <span className="absolute -top-1 -end-1 w-3.5 h-3.5 bg-gold rounded-full border-2 border-white animate-pulse" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 end-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[75vh] bg-white rounded-3xl shadow-2xl border border-navy/10 flex flex-col overflow-hidden"
          >
            <div className="bg-navy text-white p-4 flex items-center gap-3">
              <img src="/jose.png" alt="José" className="w-12 h-12 rounded-full object-cover border-2 border-gold" />
              <div className="flex-1">
                <div className="font-display text-lg leading-tight">José</div>
                <div className="text-xs text-white/60 flex items-center gap-1.5"><span className="w-2 h-2 bg-green-400 rounded-full" /> Conseiller YADIMMO · en ligne</div>
              </div>
              <button data-testid="assistant-callback-open" onClick={() => { setShowCallback(true); setCbSent(false); }} title="Être rappelé" className="p-1.5 hover:text-gold"><PhoneCall size={20} /></button>
              <button data-testid="assistant-close" onClick={() => setOpen(false)} className="p-1 hover:text-gold"><X size={22} /></button>
            </div>

            {showCallback && (
              <div data-testid="callback-panel" className="absolute inset-x-0 bottom-0 top-[76px] bg-white z-10 flex flex-col p-5 overflow-y-auto">
                <button data-testid="callback-back" onClick={() => setShowCallback(false)} className="self-start text-sm text-navy/60 hover:text-navy inline-flex items-center gap-1 mb-4"><ArrowLeft size={16} /> Retour au chat</button>
                {cbSent ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="callback-success">
                    <CheckCircle size={48} weight="light" className="text-gold" />
                    <div className="font-display text-xl text-navy">Merci {cb.first} !</div>
                    <p className="text-sm text-muted-foreground max-w-xs">Un conseiller YADIMMO vous rappellera très rapidement au {cb.phone}.</p>
                  </div>
                ) : (
                  <>
                    <div className="font-display text-lg text-navy mb-1">Être rappelé par un conseiller</div>
                    <p className="text-sm text-muted-foreground mb-4">Tous nos conseillers sont actuellement occupés. Laissez vos coordonnées, on vous rappelle rapidement.</p>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="cb-first" placeholder="Prénom *" value={cb.first} onChange={(e) => setCb({ ...cb, first: e.target.value })} className="px-3 py-2.5 rounded-xl border border-navy/15 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                        <input data-testid="cb-last" placeholder="Nom" value={cb.last} onChange={(e) => setCb({ ...cb, last: e.target.value })} className="px-3 py-2.5 rounded-xl border border-navy/15 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                      </div>
                      <input data-testid="cb-phone" placeholder="Téléphone *" value={cb.phone} onChange={(e) => setCb({ ...cb, phone: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-navy/15 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                      <input data-testid="cb-email" placeholder="Email" value={cb.email} onChange={(e) => setCb({ ...cb, email: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-navy/15 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                      <button data-testid="cb-submit" onClick={submitCallback} disabled={cbSending} className="w-full bg-navy text-white font-semibold py-3 rounded-full hover:bg-[#254B75] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"><PhoneCall size={18} /> {cbSending ? "Envoi…" : "Être rappelé"}</button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-navy text-white rounded-br-sm" : "bg-white border border-navy/10 text-navy rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-navy/10 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1">
                    <span className="w-2 h-2 bg-navy/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-navy/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-navy/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-navy/10 bg-white flex items-center gap-2">
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrivez votre message…"
                className="flex-1 px-4 py-2.5 rounded-full border border-navy/15 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
              />
              <button data-testid="assistant-send" onClick={send} disabled={loading} className="w-10 h-10 flex items-center justify-center bg-gold text-navy rounded-full hover:bg-[#C8924A] transition-colors disabled:opacity-50">
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AssistantJose;
